import { useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { Button, Loader, Modal, RadioField, TextField, Tooltip } from '@components';
import { ScreenSizeContext } from '@context';
import { ExternalIdentityService } from '@data';
import { Close, Done } from '@public';
import { LinkWithReferencesPayload } from '@types';
import { FormErrors, useLogout, validateFormSchema } from '@utils';
import * as Yup from 'yup';

import styles from './wehRegistration.module.scss';

type RadioOption = 'authorisation' | 'meter';

interface WehRegistrationFormValues {
	clientReference?: string;
	referenceOption?: RadioOption;
	authorisationReference?: string;
	meterReference?: string;
}

const wehRegistrationSchema = Yup.object().shape({
	clientReference: Yup.string().required('Client reference number is required'),
	referenceOption: Yup.string().required('Please choose a reference number option'),
	authorisationReference: Yup.string().when('referenceOption', {
		is: (val: string) => val && val == 'authorisation',
		then: schema => schema.required('Authorisation reference number is required'),
		otherwise: schema => schema.nullable().optional(),
	}),
	meterReference: Yup.string().when('referenceOption', {
		is: (val: string) => val && val == 'meter',
		then: schema => schema.required('Device reference number is required'),
		otherwise: schema => schema.nullable().optional(),
	}),
});

export interface WehRegistrationProps {
	isForAdditionalLinking?: boolean;
	onClickBack?: () => void;
}

export const WehRegistration = ({
	isForAdditionalLinking: isForAdditional,
	onClickBack = () => {
		return;
	},
}: WehRegistrationProps) => {
	const router = useRouter();

	// Context
	const { isSmallDT, isMobile } = useContext(ScreenSizeContext);

	// State
	const [formValues, setFormValues] = useState<WehRegistrationFormValues>({});
	const [formErrors, setFormErrors] = useState<FormErrors<WehRegistrationFormValues>>({});
	const [isValidForm, setIsValidForm] = useState(false);

	// Others
	const logout = useLogout();

	// Data
	const linkExternalIdentity = useMutation({
		mutationFn: (payload: LinkWithReferencesPayload) =>
			ExternalIdentityService.linkWithReferences(payload),
	});

	// Helpers
	const onSubmit = async () => {
		const isValidForm = await validateForm();
		if (isValidForm) {
			const data: LinkWithReferencesPayload = castFormValues();
			await linkExternalIdentity.mutateAsync(data);
		} else {
			showFormErrors();
		}
	};

	const castFormValues = (): LinkWithReferencesPayload => {
		const preData = { ...formValues };
		if (preData.referenceOption != 'authorisation') {
			delete preData.authorisationReference;
		} else {
			delete preData.meterReference;
		}
		delete preData.referenceOption;
		return preData as LinkWithReferencesPayload;
	};

	const extractFormErrors = useCallback(async () => {
		const nextFormErrors: FormErrors<WehRegistrationFormValues> = await validateFormSchema(
			wehRegistrationSchema,
			formValues,
		);
		return nextFormErrors;
	}, [formValues]);

	const validateForm = useCallback(async () => {
		const nextFormErrors = await extractFormErrors();
		return Object.keys(nextFormErrors).length == 0;
	}, [extractFormErrors]);

	const showFormErrors = async () => {
		const nextFormErrors = await extractFormErrors();
		setFormErrors({ ...nextFormErrors });
	};

	const navigateToContact = () => {
		router.push('/contact');
	};

	const navigateToLinkings = () => {
		router.push('/inbox');
	};

	// Life cycle
	useEffect(() => {
		const checkValidity = async () => {
			const nextIsValidForm = await validateForm();
			setIsValidForm(nextIsValidForm);
		};
		checkValidity();
	}, [formValues, validateForm]);

	const InputsContent = () => (
		<>
			<h2 className={styles.title}></h2>
			<p>Please enter the following information so we can set up your WaterIQ account.</p>

			<div className={[styles['form-container'], styles.grid].join(' ')}>
				<div className={[styles.cell, styles['cell-half-left'], styles['cell-flex']].join(' ')}>
					<p>Enter your Client reference number</p>
				</div>

				{/* Client reference */}
				<div className={[styles.cell, styles['cell-half-right'], styles['cell-flex']].join(' ')}>
					<TextField
						customStyles={styles.textField}
						placeholder=""
						handleChange={e => {
							setFormValues({ ...formValues, clientReference: e.target.value });
							delete formErrors.clientReference;
						}}
						status={formErrors.clientReference ? 'error' : 'valid'}
						errorMsg={''}
						testId="clientReference"
					/>
					<Tooltip
						title={'Client reference number'}
						text={`You can find your client reference on your authorisation document or meter read reminder.`}
						id="client-reference-tooltip"
					/>
				</div>
				{formErrors.clientReference && (
					<p className={[styles.cell, styles['cell-full'], styles['cell-error']].join(' ')}>
						{formErrors.clientReference}
					</p>
				)}

				<p className={[styles.cell, styles['cell-full'], styles['cell-and']].join(' ')}>and your</p>
				{formErrors.referenceOption && (
					<p className={[styles.cell, styles['cell-full'], styles['cell-error']].join(' ')}>
						{formErrors.referenceOption}
					</p>
				)}

				{/* Authorisation reference */}
				<RadioField
					value="authorisation"
					isChecked={formValues.referenceOption == 'authorisation'}
					onChange={(value: string) => {
						setFormValues({
							...formValues,
							referenceOption: value as RadioOption,
						});
						delete formErrors.referenceOption;
					}}
					customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(' ')}
					testId="authorisationRadioButton"
				>
					Authorisation reference number
				</RadioField>
				<div className={[styles.cell, styles['cell-half-right'], styles['cell-flex']].join(' ')}>
					<TextField
						customStyles={styles.textField}
						placeholder=""
						handleChange={e => {
							setFormValues({
								...formValues,
								authorisationReference: e.target.value,
							});
							delete formErrors.authorisationReference;
						}}
						disabled={formValues.referenceOption != 'authorisation'}
						status={formErrors.authorisationReference ? 'error' : 'valid'}
						errorMsg={''}
						testId="authorisationReference"
					/>
					<Tooltip
						title={'Authorisation reference number'}
						text={`You can find your authorisation reference on your authorisation document.`}
						id="authorisation-reference-tooltip"
					/>
				</div>
				{formErrors.authorisationReference && (
					<p className={[styles.cell, styles['cell-full'], styles['cell-error']].join(' ')}>
						{formErrors.authorisationReference}
					</p>
				)}

				<p className={[styles.cell, styles['cell-full'], styles['cell-or']].join(' ')}>or</p>

				{/* Meter reference */}
				<RadioField
					value="meter"
					isChecked={formValues.referenceOption == 'meter'}
					onChange={(value: string) => {
						setFormValues({
							...formValues,
							referenceOption: value as RadioOption,
						});
						delete formErrors.referenceOption;
					}}
					customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(' ')}
					testId="meterRadioButton"
				>
					Device reference number
				</RadioField>
				<div className={[styles.cell, styles['cell-half-right'], styles['cell-flex']].join(' ')}>
					<TextField
						customStyles={styles.textField}
						placeholder=""
						handleChange={e => {
							setFormValues({ ...formValues, meterReference: e.target.value });
							delete formErrors.meterReference;
						}}
						disabled={formValues.referenceOption != 'meter'}
						status={formErrors.meterReference ? 'error' : 'valid'}
						errorMsg={''}
						testId="meterReference"
					/>
					<Tooltip
						title={'Device reference number'}
						text={`You can find your device reference number on your meter read reminder.`}
						id="meter-reference-tooltip"
					/>
				</div>
				{formErrors.meterReference && (
					<p className={[styles.cell, styles['cell-full'], styles['cell-error']].join(' ')}>
						{formErrors.meterReference}
					</p>
				)}
			</div>

			<div className={styles['button-submit-container']}>
				<Button
					key="cancel"
					type="secondary"
					size="auto"
					onClick={onClickBack}
					disabled={linkExternalIdentity.isPending}
					data-testid="cancel"
				>
					Cancel
				</Button>
				<Button
					size="auto"
					type="primary"
					onClick={onSubmit}
					disabled={!isValidForm}
					data-testid="requestLinkingSubmit"
				>
					Submit
				</Button>
			</div>
		</>
	);

	const SuccessContent = () => (
		<div className={styles['content-centered']}>
			<div className={styles['row-content']}>
				<Done customStyles={[styles.icon, styles['icon-success']].join(' ')} />
				<div>
					<h3>Submitted</h3>
					<p>
						Thank you for your submission.{' '}
						{isForAdditional ? (
							<span>
								Your reference number is <b>{linkExternalIdentity.data?.reference}</b>.
							</span>
						) : (
							<></>
						)}
					</p>
					<p>Please allow 2 business days for your request to be processed.</p>
				</div>
			</div>
			<div className={styles['button-modal-row']}>
				{isForAdditional ? (
					<>
						<Button
							size="large"
							type="secondary"
							onClick={navigateToLinkings}
							customStyles={styles['button-view']}
							data-testid="viewSubmittedRequests"
						>
							View Submitted requests
						</Button>
						<Button
							customStyles={[styles['button-separate'], styles['button-larger']].join(' ')}
							size="large"
							type="primary"
							onClick={onClickBack}
							data-testid="backToAccountDetails"
						>
							{isSmallDT || isMobile ? 'Back' : 'Back to Account details'}
						</Button>
					</>
				) : (
					<Button size="small" type="primary" onClick={logout} data-testid="linkToClientLogOut">
						Logout
					</Button>
				)}
			</div>
		</div>
	);

	const FailureModal = () => {
		return (
			<Modal customModalStyle={styles.modal} open={linkExternalIdentity.isError}>
				<>
					<div className={styles['row-content']}>
						<Close customStyles={[styles.icon, styles['icon-error']].join(' ')} />
						<div>
							<p>Details provided were unable to be verified.</p>
						</div>
					</div>
					<div className={styles['button-modal-row']}>
						<Button
							size="small"
							type="primary"
							onClick={() => linkExternalIdentity.reset()}
							data-testid={'tryAgain'}
						>
							Try again
						</Button>
						<Button
							customStyles={[styles['button-separate'], styles['button-larger']].join(' ')}
							size="large"
							type="secondary"
							onClick={navigateToContact}
							data-testid={'contactUs'}
						>
							{isSmallDT || isMobile ? 'Contact us' : 'Contact us for assistance'}
						</Button>
					</div>
				</>
			</Modal>
		);
	};

	return linkExternalIdentity.isIdle ? (
		InputsContent()
	) : linkExternalIdentity.isPending ? (
		<div className={styles.loader}>
			<Loader />
		</div>
	) : linkExternalIdentity.isSuccess ? (
		SuccessContent()
	) : (
		FailureModal()
	);
};
